/**
 * Rovain — global storefront JS.
 * Handles: transparent header on scroll, mobile nav drawer,
 * cart drawer + fetch-based add-to-cart, carousel arrows,
 * announcement rotator, predictive search, quantity steppers,
 * collection filters.
 */
(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  // ─── Transparent header on scroll + live header-height var ─────
  function initHeader() {
    const header = $('.site-header');
    if (!header) return;

    const syncHeight = () => {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    };
    syncHeight();
    if ('ResizeObserver' in window) new ResizeObserver(syncHeight).observe(header);
    window.addEventListener('resize', syncHeight);

    if (!document.body.classList.contains('has-transparent-header')) {
      header.classList.add('site-header--solid');
      return;
    }
    // Transparent only while the page sits at the very top: a 1px sentinel at
    // the top of the document leaves the viewport as soon as the user scrolls.
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;height:1px;width:1px;pointer-events:none';
    document.body.appendChild(sentinel);
    const io = new IntersectionObserver(([e]) => {
      header.classList.toggle('site-header--solid', !e.isIntersecting);
    }, { threshold: 0 });
    io.observe(sentinel);
  }

  // ─── Mobile nav drawer ─────────────────────────────────────────
  function initMobileNav() {
    const toggle = $('[data-nav-toggle]');
    const dialog = $('#MobileNavDrawer');
    if (!toggle || !dialog) return;
    toggle.addEventListener('click', () => dialog.showModal());
    $$('[data-nav-close]', dialog).forEach(b => b.addEventListener('click', () => dialog.close()));
  }

  // ─── Cart drawer ───────────────────────────────────────────────
  const CartDrawer = {
    open() {
      const d = $('#CartDrawer');
      if (d && !d.open) d.showModal();
    },
    close() {
      const d = $('#CartDrawer');
      if (d && d.open) d.close();
    },
    async refresh() {
      const drawer = $('#CartDrawer');
      if (!drawer) return;
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-drawer`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('#CartDrawer .cart-drawer__inner');
        const cur = drawer.querySelector('.cart-drawer__inner');
        if (next && cur) cur.replaceWith(next);
        this.updateBubble();
      } catch (e) { /* silent — form fallback will full-reload */ }
    },
    async updateBubble() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        $$('.cart-count').forEach(el => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      } catch (e) {}
    }
  };
  window.RovainCart = CartDrawer;

  function initCartDrawer() {
    const drawer = $('#CartDrawer');
    if (!drawer) return;
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-cart-open]');
      if (opener) { e.preventDefault(); CartDrawer.open(); return; }
      const closer = e.target.closest('[data-cart-close]');
      if (closer && drawer.contains(closer)) { e.preventDefault(); CartDrawer.close(); }
    });
  }

  function initAddToCartForms() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('[type=submit]');
      if (btn) { btn.dataset.originalLabel = btn.textContent; btn.disabled = true; btn.textContent = btn.dataset.addingLabel || '...'; }
      try {
        const res = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('add failed');
        await CartDrawer.refresh();
        CartDrawer.open();
      } catch (err) {
        form.submit();
        return;
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = btn.dataset.originalLabel || btn.textContent; }
      }
    });

    document.addEventListener('change', async (e) => {
      const input = e.target.closest('[data-cart-quantity]');
      if (!input) return;
      const key = input.dataset.cartQuantity;
      const qty = parseInt(input.value, 10);
      try {
        await fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        });
        await CartDrawer.refresh();
      } catch (err) { /* silent */ }
    });

    document.addEventListener('click', async (e) => {
      const link = e.target.closest('[data-cart-remove]');
      if (!link) return;
      e.preventDefault();
      const key = link.dataset.cartRemove;
      try {
        await fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        await CartDrawer.refresh();
      } catch (err) { window.location.href = link.href; }
    });
  }

  // ─── Carousel arrows ───────────────────────────────────────────
  function initCarousels() {
    $$('[data-carousel]').forEach(carousel => {
      const track = $('[data-carousel-track]', carousel);
      const prev = $('[data-carousel-prev]', carousel);
      const next = $('[data-carousel-next]', carousel);
      if (!track) return;
      const step = () => Math.max(320, track.clientWidth * 0.7);
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    });
  }

  // ─── Announcement rotator ──────────────────────────────────────
  function initAnnouncement() {
    $$('[data-announcement]').forEach(el => {
      const items = $$('[data-announcement-item]', el);
      if (items.length <= 1) return;
      let i = 0;
      const interval = parseInt(el.dataset.interval || '4000', 10);
      const show = (n) => items.forEach((it, k) => { it.hidden = k !== n; });
      show(0);
      const step = (d) => { i = (i + d + items.length) % items.length; show(i); };
      el.querySelector('[data-announcement-prev]')?.addEventListener('click', () => step(-1));
      el.querySelector('[data-announcement-next]')?.addEventListener('click', () => step(1));
      setInterval(() => step(1), interval);
    });
  }

  // ─── Complete Looks tab switcher ───────────────────────────────
  function initLooks() {
    $$('[data-looks]').forEach(root => {
      const tabs = $$('[data-look-tab]', root);
      const panels = $$('[data-look-panel]', root);
      const activate = (idx) => {
        tabs.forEach((t, i) => t.setAttribute('aria-selected', i === idx));
        panels.forEach((p, i) => p.hidden = i !== idx);
      };
      tabs.forEach((t, i) => t.addEventListener('click', () => activate(i)));
      activate(0);
    });
  }

  // ─── Quantity stepper ──────────────────────────────────────────
  function initQty() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-step]');
      if (!btn) return;
      const input = btn.parentElement.querySelector('input');
      if (!input) return;
      const delta = parseInt(btn.dataset.qtyStep, 10);
      input.value = Math.max(parseInt(input.min || '1', 10), (parseInt(input.value, 10) || 1) + delta);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // ─── Predictive search ────────────────────────────────────────
  function initPredictive() {
    const overlay = $('#SearchOverlay');
    if (!overlay) return;
    const input = $('input[type=search]', overlay);
    const results = $('[data-search-results]', overlay);
    let ctrl;
    $$('[data-search-open]').forEach(el =>
      el.addEventListener('click', (e) => { e.preventDefault(); overlay.showModal(); setTimeout(() => input?.focus(), 40); })
    );
    $$('[data-search-close]', overlay).forEach(el => el.addEventListener('click', () => overlay.close()));
    input?.addEventListener('input', async (e) => {
      const q = e.target.value.trim();
      if (!q) { if (results) results.innerHTML = ''; return; }
      ctrl?.abort();
      ctrl = new AbortController();
      try {
        const res = await fetch(`/search/suggest?q=${encodeURIComponent(q)}&resources[type]=product,collection,article&resources[limit]=6&section_id=search-suggest`, { signal: ctrl.signal });
        const html = await res.text();
        const frag = new DOMParser().parseFromString(html, 'text/html');
        const next = frag.querySelector('[data-search-results]');
        if (next && results) results.innerHTML = next.innerHTML;
      } catch (err) { /* aborted */ }
    });
  }

  // ─── Colour swatches on product cards ──────────────────────────
  // Every colour is its own product, so a card represents a colour group:
  // hovering a swatch previews that colour, clicking it selects the colour
  // without leaving the page. Without JS the swatches stay plain links.
  function initColourSwatches() {
    $$('.product-card').forEach(card => {
      const swatches = $$('[data-colour-swatch]', card);
      if (swatches.length < 2) return;
      const link = $('[data-card-link]', card);
      const primary = $('.product-card__primary', card);
      if (!link || !primary) return;
      const alt = $('.product-card__alt', card);

      const base = {
        href: link.getAttribute('href'),
        src: primary.getAttribute('src'),
        srcset: primary.getAttribute('srcset')
      };
      let picked = null;

      // The swatches sit inside the card, so hovering one also triggers the
      // card's hover state: the primary image fades out and the second image
      // fades in. While a colour is shown both are overridden — the second
      // image belongs to another colour, and an unpinned primary would leave
      // the empty card background on screen.
      const render = (hovered) => {
        const source = hovered || picked;
        const image = source && source.dataset.colourImage;
        link.setAttribute('href', (source && source.getAttribute('href')) || base.href);
        if (image) {
          primary.setAttribute('src', image);
          primary.removeAttribute('srcset');
          primary.style.opacity = '1';
          if (alt) alt.hidden = true;
        } else {
          primary.setAttribute('src', base.src);
          if (base.srcset) primary.setAttribute('srcset', base.srcset);
          else primary.removeAttribute('srcset');
          primary.style.removeProperty('opacity');
          if (alt) alt.hidden = false;
        }
      };

      swatches.forEach(swatch => {
        swatch.addEventListener('mouseenter', () => render(swatch));
        swatch.addEventListener('focus', () => render(swatch));
        swatch.addEventListener('mouseleave', () => render(null));
        swatch.addEventListener('blur', () => render(null));
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          picked = swatch;
          swatches.forEach(s => {
            const on = s === swatch;
            s.classList.toggle('border-foreground', on);
            s.classList.toggle('border-transparent', !on);
            if (on) s.setAttribute('aria-current', 'true');
            else s.removeAttribute('aria-current');
          });
          render(swatch);
        });
      });
    });
  }

  // ─── Collection / search filters ───────────────────────────────
  // The filter form only carries a submit button inside <noscript>, so with
  // JS the form never submits on its own: a change to a checkbox, a price or
  // the sort order rebuilds the query string and navigates. Empty fields are
  // dropped so the URL only ever carries live filters, and `page` is left out
  // so filtering always lands on the first page of results.
  function initFacets() {
    $$('form[data-facets]').forEach(form => {
      // Without JS the sort select falls back to its inline submit; here we
      // take over, so blank price fields never end up in the URL.
      $('select[name=sort_by]', form)?.removeAttribute('onchange');

      const apply = () => {
        const params = new URLSearchParams();
        new FormData(form).forEach((value, key) => {
          if (String(value).trim() !== '') params.append(key, value);
        });
        const action = form.getAttribute('action');
        const path = action ? action.split('?')[0] : window.location.pathname;
        const query = params.toString();
        window.location.href = query ? `${path}?${query}` : path;
      };

      form.addEventListener('change', apply);
      // Number inputs don't trigger implicit submission while the form has
      // several of them, so Enter is wired up by hand.
      form.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || !e.target.matches('input[type=number]')) return;
        e.preventDefault();
        apply();
      });
    });
  }

  // ─── Newsletter ────────────────────────────────────────────────
  function initNewsletter() {
    // Shopify handles submission; we just style the state via [data-success]/[data-error]
    // via server-rendered messages; nothing extra needed here.
  }

  function init() {
    initHeader();
    initMobileNav();
    initCartDrawer();
    initAddToCartForms();
    initCarousels();
    initAnnouncement();
    initLooks();
    initQty();
    initPredictive();
    initColourSwatches();
    initFacets();
    initNewsletter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else init();
})();
